import { Box, Stack } from "@mui/material";
import { IWikifolioDetailProps } from "src/api/server/wf-detail.types";
import { LeverageProductsModal } from "src/components/modals/leverage-products-modal/leverage-products-modal";
import { Ad } from "src/components/pages/detail/ad/ad";
import {
    GENERAL_SECTION_SPACING,
    HIDE_CONTENT_CHART_TEST_ID,
    HIDE_CONTENT_PORTFOLIO_DISTRIBUTION_TEST_ID,
    HIDE_CONTENT_PORTFOLIO_TABLE_TEST_ID,
    MAIN_VIEW_WIDTH,
} from "src/components/pages/detail/constants";
import { DownloadSection } from "src/components/pages/detail/download-section";
import { FeedSection } from "src/components/pages/detail/feed/feed-section";
import { HideContentWithResponsiveImages as HiddenContent } from "src/components-mui/common/hide-content/hide-content-with-image";
import { JumpMarkSection, JumpMarkSharedSection } from "src/components-mui/common/jump-marks/jump-mark-section";
import { SelectiveHydrationBoundary } from "src/components-mui/common/selective-hydration-boundary/selective-hydration-boundary";
import { Container } from "src/components-mui/layout/container";
import { CertificateBox } from "src/components-mui/pages/detail/certificate-box/certificate-box";
import { PortfolioDownloadPrompt } from "src/components-mui/pages/detail/certificate-box/components/portfolio-download-prompt/portfolio-download-prompt";
import { DetailBenchmark } from "src/components-mui/pages/detail/detail-benchmark";
import { DetailJumpMark, DetailJumpMarksBar, DetailJumpMarksProvider } from "src/components-mui/pages/detail/detail-jump-marks";
import { KeyFigures } from "src/components-mui/pages/detail/key-figures/key-figures";
import { NewsListSection } from "src/components-mui/pages/detail/news/news-list-section";
import { PortfolioChartWrapper } from "src/components-mui/pages/detail/portfolio-chart/portfolio-chart-wrapper";
import { PortfolioTableSection } from "src/components-mui/pages/detail/portfolio-table-section/portfolio-table-section";
import { SalesSection } from "src/components-mui/pages/detail/sales-section/sales-section";
import { SelectedWikifolios } from "src/components-mui/pages/detail/selected-wikifolios/selected-wikifolios";
import { TagSection } from "src/components-mui/pages/detail/tag-section";
import { TopKpi } from "src/components-mui/pages/detail/top-kpi/top-kpi";
import { TopSection } from "src/components-mui/pages/detail/top-section";
import { TradeHistorySection } from "src/components-mui/pages/detail/trade-history/trade-history-section";
import { TradingIdeaSection } from "src/components-mui/pages/detail/trading-idea-section/trading-idea-section";
import { TreemapChartSection } from "src/components-mui/pages/detail/treemap-chart/treemap-chart-section";
import { LANGUAGE } from "src/constants/locale";
import { useRouterLocale } from "src/utils/router/use-router-locale";
import { PortfolioDistributionSection } from "./portfolio-distribution-section/portfolio-distribution-section";
import { PromoCard } from "./promo-card/promo-card";
import { RelatedWikifolios } from "./related-wikifolios/related-wikifolios";
import { WatchlistCtaSection } from "./watchlist-cta/watchlist-cta-section";

interface IWikifolioDetailPage extends Pick<IWikifolioDetailProps, "data" | "dict" | "head"> {
    isLoggedIn: boolean;
    wikifolioId: string;
}

export const WikifolioDetailPage = ({ data, dict, head, isLoggedIn, wikifolioId }: IWikifolioDetailPage) => {
    const { language } = useRouterLocale();

    return (
        <>
            <Stack sx={{ mb: [5, 10] }}>
                <DetailJumpMarksProvider labels={dict.jumpMarks}>
                    <Container sx={{ order: ["initial", -1] }}>
                        <JumpMarkSection section={DetailJumpMark.Overview} anchor>
                            <TopSection
                                data={data}
                                headData={{ metaTitle: head.title, metaDescription: head.description, url: head.canonicalUrl }}
                                sx={{ mt: [5, 5, 7], mb: [3, 4] }}
                            />
                        </JumpMarkSection>
                    </Container>
                    <DetailJumpMarksBar sx={{ order: -1, mb: { xs: 0, sm: 4 } }} />
                    <Container>
                        <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: ["column", "column", "row"] }}>
                            <Box sx={{ width: MAIN_VIEW_WIDTH }}>
                                <JumpMarkSection section={DetailJumpMark.Overview} sx={{ overflow: { xs: "hidden", sm: "visible" } }}>
                                    {isLoggedIn && (
                                        <DetailBenchmark
                                            wikifolioId={data.wikifolio.id}
                                            wikifolioStatus={data.wikifolio.status}
                                            creationDate={data.masterData.creationDate.value}
                                            baseCurrency={data.wikifolio.currency}
                                            dict={dict.benchmark}
                                            sx={{ mb: { xs: 5, md: 7 } }}
                                        />
                                    )}

                                    {!isLoggedIn && (
                                        <HiddenContent
                                            responsiveImages={data.loggedOutPreviewImages?.chart}
                                            srcSetDimensions={[
                                                { width: 670, height: 454 },
                                                { width: 670, height: 454 },
                                                { width: 604, height: 454 },
                                                { width: 752, height: 454 },
                                            ]}
                                            sx={{ marginBottom: [5, 5, 7] }}
                                            topOffset={["57px", "90px"]}
                                            containerHeight="454px"
                                            loading="eager"
                                            title={dict.hideContent.title}
                                            description={dict.hideContent.descriptionChart}
                                            dataTestId={HIDE_CONTENT_CHART_TEST_ID}
                                        />
                                    )}
                                    <TopKpi keyFigures={data.keyFigures.topKpis} />
                                    <PortfolioChartWrapper
                                        wikifolioId={wikifolioId}
                                        dict={dict.portfolioChart}
                                        sx={{ my: GENERAL_SECTION_SPACING }}
                                    />

                                    {data.displayPortfolioDistributionChart && isLoggedIn && (
                                        <PortfolioDistributionSection
                                            sx={{ my: GENERAL_SECTION_SPACING }}
                                            dict={dict.portfolioDistribution}
                                            wikifolioId={wikifolioId}
                                        />
                                    )}

                                    {!isLoggedIn && data.displayPortfolioDistributionChart && (
                                        <HiddenContent
                                            responsiveImages={data.loggedOutPreviewImages?.portfolioDistribution}
                                            srcSetDimensions={[
                                                { width: 606, height: 476 },
                                                { width: 670, height: 476 },
                                                { width: 604, height: 476 },
                                                { width: 752, height: 476 },
                                            ]}
                                            sx={{ marginBottom: [10, 10, 14] }}
                                            objectFit={["contain", "unset"]}
                                            topOffset={["173px", "132px", "120px", "101px"]}
                                            containerHeight="476px"
                                            title={dict.hideContent.title}
                                            description={dict.hideContent.descriptionPortfolioDistribution}
                                            dataTestId={HIDE_CONTENT_PORTFOLIO_DISTRIBUTION_TEST_ID}
                                        />
                                    )}
                                </JumpMarkSection>
                            </Box>
                            <Box
                                component="aside"
                                sx={{
                                    position: "relative",
                                    width: ["auto", "auto", "278px", "290px"],
                                    height: ["auto", "auto", 0],
                                    mb: [10, 10, 0],
                                    zIndex: 1,
                                }}
                            >
                                <CertificateBox dict={dict.certificate} data={data} />
                                {data.wikifolio?.isParadise ? (
                                    <Box sx={{ pt: 3 }}>
                                        <RelatedWikifolios wikifolioId={wikifolioId} dict={dict.relatedWikifolios} />
                                    </Box>
                                ) : null}
                                {data?.ad && (
                                    <Box sx={{ pt: 3, display: ["none", "none", "block"] }}>
                                        <Ad {...data.ad} />
                                    </Box>
                                )}

                                {data?.onboardingArticle ? (
                                    <Box sx={{ pt: 3 }}>
                                        <PromoCard
                                            title={data.onboardingArticle.title}
                                            description={data.onboardingArticle.description}
                                            image={data.onboardingArticle.image}
                                            link={data.onboardingArticle.link}
                                        />
                                    </Box>
                                ) : null}
                            </Box>
                        </Box>
                    </Container>

                    <Container>
                        <Box sx={{ width: MAIN_VIEW_WIDTH }}>
                            {(data?.tagsData || data?.salesContent) && (
                                <Box sx={{ mb: [10, 10, 14] }}>
                                    <JumpMarkSection section={DetailJumpMark.Overview}>
                                        <SelectiveHydrationBoundary>
                                            {data.salesContent && <SalesSection data={data.salesContent} sx={{ mb: GENERAL_SECTION_SPACING }} />}
                                            {data.tagsData && dict.tagSection && <TagSection data={data.tagsData} dict={dict.tagSection} />}
                                        </SelectiveHydrationBoundary>
                                    </JumpMarkSection>
                                </Box>
                            )}

                            <Box sx={{ mb: [10, 10, 14] }}>
                                <JumpMarkSection section={DetailJumpMark.Feed} anchor>
                                    <SelectiveHydrationBoundary>
                                        <FeedSection wikifolioId={wikifolioId} traderId={data.trader.id} dict={dict.feed} />
                                    </SelectiveHydrationBoundary>
                                </JumpMarkSection>
                            </Box>
                        </Box>
                    </Container>

                    {data.watchlistCta.first && (
                        <SelectiveHydrationBoundary>
                            <WatchlistCtaSection
                                component="section"
                                sx={{ zIndex: 0 }}
                                wikifolioId={wikifolioId}
                                wikifolioShortDescription={data.wikifolio.shortDescription}
                                isOnWatchlist={data.wikifolio.isOnWatchlist}
                                data={data.watchlistCta.first}
                            />
                        </SelectiveHydrationBoundary>
                    )}

                    <Container>
                        <Box sx={{ width: MAIN_VIEW_WIDTH, mb: [10, 10, 14] }}>
                            <Box sx={{ mb: [10, 10, 14] }}>
                                <JumpMarkSection section={DetailJumpMark.TradingIdea} anchor>
                                    <SelectiveHydrationBoundary>
                                        <TradingIdeaSection
                                            dict={dict}
                                            isTranslationNecessary={data.isTranslationNecessary}
                                            description={data.wikifolio.description}
                                            masterData={data.masterData}
                                            investmentUniverseData={data.investmentUniverseData}
                                        />
                                    </SelectiveHydrationBoundary>
                                </JumpMarkSection>
                            </Box>

                            {isLoggedIn && (
                                <>
                                    <Box sx={{ mb: [10, 10, 14] }}>
                                        <JumpMarkSection section={DetailJumpMark.Portfolio} anchor>
                                            <SelectiveHydrationBoundary>
                                                <PortfolioTableSection fullName={data.wikifolio.fullName} dict={dict.portfolioTable} />
                                            </SelectiveHydrationBoundary>
                                        </JumpMarkSection>
                                    </Box>

                                    {language === LANGUAGE.DE && (
                                        <Box sx={{ mb: [10, 10, 14] }}>
                                            <JumpMarkSection section={DetailJumpMark.Portfolio}>
                                                <SelectiveHydrationBoundary>
                                                    <NewsListSection
                                                        wikifolioId={wikifolioId}
                                                        headline={dict.newsFeed.headline}
                                                        emptyState={dict.newsFeed.empty}
                                                    />
                                                </SelectiveHydrationBoundary>
                                            </JumpMarkSection>
                                        </Box>
                                    )}

                                    <Box sx={{ mb: [10, 10, 14] }}>
                                        <JumpMarkSection section={DetailJumpMark.KeyFigures} anchor>
                                            <SelectiveHydrationBoundary>
                                                <KeyFigures keyFigures={data.keyFigures} dict={dict} />
                                            </SelectiveHydrationBoundary>
                                        </JumpMarkSection>
                                    </Box>
                                    <JumpMarkSection section={DetailJumpMark.KeyFigures}>
                                        <SelectiveHydrationBoundary>
                                            <TreemapChartSection
                                                wikifolioStatus={data.wikifolio.status}
                                                dict={dict.pnlChart}
                                                symbol={data.wikifolio.symbol}
                                                creationDate={data.masterData.creationDate.value}
                                            />
                                        </SelectiveHydrationBoundary>
                                    </JumpMarkSection>
                                </>
                            )}
                        </Box>
                    </Container>
                    {data.watchlistCta.second && (
                        <SelectiveHydrationBoundary>
                            <WatchlistCtaSection
                                component="section"
                                sx={{ zIndex: 0 }}
                                wikifolioId={wikifolioId}
                                wikifolioShortDescription={data.wikifolio.shortDescription}
                                isOnWatchlist={data.wikifolio.isOnWatchlist}
                                data={data.watchlistCta.second}
                            />
                        </SelectiveHydrationBoundary>
                    )}
                    <Container>
                        <Box sx={{ width: MAIN_VIEW_WIDTH }}>
                            {isLoggedIn && (
                                <>
                                    <Box sx={{ mb: [10, 10, 14] }}>
                                        <JumpMarkSection section={DetailJumpMark.Trades} anchor>
                                            <SelectiveHydrationBoundary>
                                                <TradeHistorySection wikifolioId={wikifolioId} dict={dict.tradeHistoryTable} />
                                            </SelectiveHydrationBoundary>
                                        </JumpMarkSection>
                                    </Box>

                                    {/* Dev-Note: there is no separate jumpmark section for the download area, therefore we keep it under the last section */}
                                    <JumpMarkSection section={DetailJumpMark.Trades}>
                                        <SelectiveHydrationBoundary>
                                            <DownloadSection data={data} dict={dict.downloadArea} />
                                        </SelectiveHydrationBoundary>
                                    </JumpMarkSection>
                                </>
                            )}

                            {!isLoggedIn && (
                                <JumpMarkSharedSection
                                    section={DetailJumpMark.Portfolio}
                                    sharedWith={[DetailJumpMark.KeyFigures, DetailJumpMark.Trades]}
                                >
                                    <SelectiveHydrationBoundary>
                                        <HiddenContent
                                            responsiveImages={data.loggedOutPreviewImages?.portfolioTable}
                                            objectFit="unset"
                                            sx={{ marginBottom: [0, 0, 5] }}
                                            containerHeight={["840px", "953px", "1008px", "972px"]}
                                            topOffset={["177px", "160px", "150px", "150px"]}
                                            title={dict.hideContent.title}
                                            description={dict.hideContent.descriptionPortfolioTable}
                                            additionalContent={<PortfolioDownloadPrompt wikifolioId={wikifolioId} />}
                                            dataTestId={HIDE_CONTENT_PORTFOLIO_TABLE_TEST_ID}
                                        />
                                    </SelectiveHydrationBoundary>
                                </JumpMarkSharedSection>
                            )}
                        </Box>
                    </Container>
                </DetailJumpMarksProvider>
            </Stack>

            <SelectiveHydrationBoundary>
                <SelectedWikifolios headline={dict.selectWikifolioTitle} selectedWikifolios={data.selectedWikifolios} />
            </SelectiveHydrationBoundary>
            <LeverageProductsModal containsLeverageProducts={data.wikifolio.containsLeverageProducts} />
        </>
    );
};
